import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useNavigate, useParams } from 'react-router-dom';
import { buildTiktokPath } from '@/tiktok-platform/constants/tiktokRoutes';
import { useTiktokShop } from '@/tiktok-platform/context/TiktokShopContext';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { extractTiktokList, pickField } from '@/tiktok-platform/utils/tiktokResponse';
import {
  detailBasicRows,
  detailPriceRows,
  detailReceiveRows,
  detailTimeRows,
  displayValue,
  formatTrackTime,
  listOrderProductSummary,
  normalizeImageUrl,
  salesStatusLabel,
  tiktokOrderActions,
  advanceOrderStatus,
  trackInfoList,
} from '@/tiktok-platform/modules/aftersales/salesOrderDisplay';
import TiktokProductThumb from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { showToast } from '@/utils/toast';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

function statusModifier(code) {
  const s = String(code || '').toUpperCase();
  if (s === 'COMPLETED' || s === 'DELIVERED' || s === '5') return 'success';
  if (s === 'CANCELLED' || s === 'CANCELED' || s === '6' || s === '8' || s === '9') return 'failed';
  return 'pending';
}

/** 平台无轨迹（9999002）或同步跳过：对用户只展示友好空态，不抛原始 API 文案。 */
function isTrackUnavailable(snap) {
  if (!snap || typeof snap !== 'object') return false;
  if (snap.skipped) return true;
  const text = [snap.error, snap.reason, snap.message]
    .filter(Boolean)
    .map((v) => String(v))
    .join(' ');
  return /9999002|数据不存在|无轨迹|not\s*found|no\s*data/i.test(text);
}

function trackEmptyMessage(snap) {
  if (isTrackUnavailable(snap)) {
    return i18n.t('orderList.noTrackData', { ns: 'workbench' });
  }
  if (snap?.error) {
    return i18n.t('orderList.noTrack', { ns: 'workbench' });
  }
  return i18n.t('orderList.noTrackNodeShort', { ns: 'workbench' });
}

function extractDetailRecord(response, orderNo) {
  if (!response) return null;
  if (response.order && typeof response.order === 'object') {
    return response.order;
  }
  const list = extractTiktokList(response);
  if (list.length) {
    const matched = list.find((row) => String(pickField(row, 'orderNo', 'orderNumber')) === String(orderNo));
    return matched || list[0];
  }
  if (typeof response === 'object' && !Array.isArray(response)) {
    if (pickField(response, 'orderNo', 'orderNumber') || response.orderGoodsInfoList) {
      return response;
    }
  }
  return null;
}

function hasRichDetail(record) {
  if (!record || typeof record !== 'object') return false;
  if (Array.isArray(record.orderGoodsInfoList) && record.orderGoodsInfoList.length) return true;
  if (Array.isArray(record.line_items) && record.line_items.length) return true;
  return Boolean(pickField(record, 'paymentTime', 'estimatedGrossIncome', 'packageWaybillList', 'salesSite', 'orderAmount'));
}

function collectPackageIds(detail) {
  const ids = [];
  const push = (value) => {
    const id = String(value || '').trim();
    if (id && !ids.includes(id)) ids.push(id);
  };
  const lists = [detail?.packages, detail?.package_list, detail?.packageWaybillList];
  lists.forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((row) => push(row?.id || row?.package_id || row?.packageId));
  });
  if (Array.isArray(detail?.line_items)) {
    detail.line_items.forEach((item) => push(item?.package_id || item?.packageId));
  }
  return ids;
}

function goodsAttrText(goods) {
  const attrs = goods?.skuAttribute;
  if (!Array.isArray(attrs) || !attrs.length) return '';
  const byLang = new Map();
  attrs.forEach((a) => {
    const lang = String(a?.language || '').toUpperCase();
    const name = a?.attrName;
    if (!name) return;
    if (!byLang.has(lang)) byLang.set(lang, []);
    byLang.get(lang).push(name);
  });
  const cn = byLang.get('CN');
  const en = byLang.get('EN') || byLang.get('US');
  const pick = cn?.length ? cn : (en?.length ? en : [...byLang.values()][0]);
  return Array.isArray(pick) ? pick.join(' / ') : '';
}

function KvTable({ rows }) {
  const visible = (rows || []).filter((r) => r && r.label && r.value != null && r.value !== '');
  if (!visible.length) {
    return <p className="tko-return-detail__empty">{i18n.t('orderList.emptyData', { ns: 'workbench' })}</p>;
  }
  return (
    <dl className="tko-order-detail-kv">
      {visible.map((row) => (
        <div key={row.label} className="tko-order-detail-kv__item">
          <dt>{row.label}</dt>
          <dd>{displayValue(row.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function TrackBlock({ snap }) {
  const tracks = trackInfoList(snap?.track || snap);
  if (!snap || isTrackUnavailable(snap) || snap?.error || !tracks.length) {
    return <div className="tko-order-track__empty">{trackEmptyMessage(snap)}</div>;
  }
  return (
    <div className="tko-order-track">
      {tracks.map((info, infoIdx) => (
        <div key={`${pickField(info, 'waybillNo') || infoIdx}`} className="tko-order-track__block">
          <div className="tko-order-track__meta">
            <span>{i18n.t('orderList.waybillPrefix', { ns: 'workbench' })}{pickField(info, 'waybillNo') || pickField(snap, 'waybillNo') || '—'}</span>
            <span>{i18n.t('orderList.carrierPrefix', { ns: 'workbench' })}{pickField(info, 'carrier') || '—'}</span>
            <span>{i18n.t('orderList.typePrefix', { ns: 'workbench' })}{pickField(info, 'waybillType') || '—'}</span>
          </div>
          <ol className="tko-order-track__timeline">
            {(Array.isArray(info.tracking) ? info.tracking : []).map((node, nodeIdx) => (
              <li key={`${nodeIdx}-${pickField(node, 'updateTimeMillis')}`}>
                <div className="tko-order-track__time">
                  {formatTrackTime(pickField(node, 'updateTimeMillis'))}
                </div>
                <div className="tko-order-track__node">
                  <strong>{pickField(node, 'nodeCodeName', 'nodeCode') || i18n.t('orderList.node', { ns: 'workbench' })}</strong>
                  <span>{pickField(node, 'description') || '—'}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

export default function OrderDetailPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const { orderNo: rawNo } = useParams();
  const orderNo = decodeURIComponent(rawNo || '').trim();
  const { activeShopId, loading: shopLoading } = useTiktokShop();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shipBusy, setShipBusy] = useState(false);
  const [trackingNo, setTrackingNo] = useState('');
  const [providerId, setProviderId] = useState('');
  const [providers, setProviders] = useState([]);
  const [priceDetail, setPriceDetail] = useState(null);

  const loadDetail = useCallback(async ({ forceLive = false } = {}) => {
    if (!activeShopId || !orderNo) return;
    setLoading(true);
    setError('');
    try {
      let record = null;
      if (!forceLive) {
        try {
          const cached = await tiktokPlatformAPI.getCachedTiktokOrder(activeShopId, orderNo);
          record = extractDetailRecord(cached, orderNo);
          if (record && !hasRichDetail(record)) {
            record = null;
          }
        } catch {
          /* 本地无完整详情再拉平台 */
        }
      }

      if (!record) {
        const live = await tiktokPlatformAPI.fetchTiktokOrderDetails(activeShopId, {
          orderNoList: [orderNo],
        });
        record = extractDetailRecord(live, orderNo);
      }
      if (!record) {
        throw new Error(t('orderList.detailMissing'));
      }

      try {
        const track = await tiktokPlatformAPI.getTiktokOrderLogisticsTrack(activeShopId, { orderNo });
        const pkgs = Array.isArray(record.packageWaybillList) && record.packageWaybillList.length
          ? record.packageWaybillList
          : (Array.isArray(track?.packageWaybillList) ? track.packageWaybillList : []);
        record = {
          ...record,
          packageWaybillList: pkgs,
          ipeLogisticsTracks: Array.isArray(track?.ipeLogisticsTracks) ? track.ipeLogisticsTracks : [],
        };
      } catch {
        /* 轨迹可选：详情仍可用 */
      }

      setDetail(record);
    } catch (e) {
      setDetail(null);
      setError(e?.message || t('orderList.detailLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeShopId, orderNo, t]);

  useEffect(() => {
    if (shopLoading) return;
    if (!activeShopId) {
      setDetail(null);
      setError(t('orderList.pickAuthorizedShop'));
      return;
    }
    if (!orderNo) {
      setError(t('orderList.invalidOrderNo'));
      return;
    }
    loadDetail();
  }, [activeShopId, orderNo, shopLoading, loadDetail, t]);

  const deliveryOptionId = String(
    detail?.delivery_option_id
    || detail?.packages?.[0]?.delivery_option_id
    || detail?.line_items?.[0]?.delivery_option_id
    || '',
  ).trim();

  useEffect(() => {
    if (!activeShopId || !deliveryOptionId) {
      setProviders([]);
      return undefined;
    }
    let cancelled = false;
    tiktokPlatformAPI.listShippingProviders(activeShopId, deliveryOptionId)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data && typeof res.data === 'object' ? res.data : res;
        const list = Array.isArray(data?.shipping_providers) ? data.shipping_providers : [];
        setProviders(list);
      })
      .catch(() => {
        if (!cancelled) setProviders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeShopId, deliveryOptionId]);

  const statusCode = pickField(detail, 'orderStatus');
  const statusLabel = salesStatusLabel(statusCode) || '—';
  const actions = tiktokOrderActions(detail);
  const goodsList = Array.isArray(detail?.orderGoodsInfoList) ? detail.orderGoodsInfoList : [];
  const packages = Array.isArray(detail?.packageWaybillList) ? detail.packageWaybillList : [];
  const tracks = Array.isArray(detail?.ipeLogisticsTracks) ? detail.ipeLogisticsTracks : [];

  const handleBack = () => {
    navigate(buildTiktokPath('orders'));
  };

  return (
    <div className="tko-return-detail" data-testid="tiktok-order-detail">
      <nav className="tko-return-detail__crumb" aria-label={t('orderList.crumb')}>
        <button type="button" className="tko-return-detail__crumb-link" onClick={handleBack}>
          {t('platformNav.orderList')}
        </button>
        <span className="tko-return-detail__crumb-sep">/</span>
        <span>{t('orderList.detailTitle')}</span>
      </nav>

      <header className="tko-return-detail__header">
        <div className="tko-return-detail__title-block">
          <h1 className="tko-return-detail__title">{t('orderList.detailTitle')}</h1>
          <div className="tko-return-detail__meta">
            <strong className="tko-return-detail__no">{orderNo || '—'}</strong>
            {detail ? (
              <span className={`tko-records__status tko-records__status--${statusModifier(statusCode)}`}>
                {statusLabel}
              </span>
            ) : null}
          </div>
        </div>
        <div className="tko-return-detail__actions">
          <button type="button" className="tko-btn tko-btn--secondary tko-btn--sm" onClick={handleBack}>
            {t('orderList.backToList')}
          </button>
          <button
            type="button"
            className="tko-btn tko-btn--primary tko-btn--sm"
            disabled={!activeShopId || !orderNo || loading}
            onClick={() => loadDetail({ forceLive: true }).then(() => showToast(t('orderList.refreshed'), 'success')).catch(() => {})}
          >
            {loading ? t('orderList.refreshing') : t('orderList.refreshFull')}
          </button>
          {actions.ship ? (
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              disabled={!activeShopId || shipBusy || collectPackageIds(detail).length === 0}
              onClick={async () => {
                const packageId = collectPackageIds(detail)[0];
                if (!packageId) {
                  showToast(t('orderList.noPackage'), 'warning');
                  return;
                }
                setShipBusy(true);
                try {
                  await tiktokPlatformAPI.shipTiktokPackage(activeShopId, packageId, {
                    handover_method: 'PICKUP',
                    order_id: orderNo,
                  });
                  showToast(t('orderList.shipSubmitted'), 'success');
                  await loadDetail();
                  setDetail((prev) => advanceOrderStatus(prev, 'AWAITING_COLLECTION'));
                } catch (e) {
                  showToast(e?.message || t('orderList.shipFailed'), 'error');
                } finally {
                  setShipBusy(false);
                }
              }}
            >
              {shipBusy ? fixedUi("发货中") : fixedUi("安排发货")}
            </button>
          ) : null}
          {actions.printLabel ? (
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              disabled={!activeShopId || shipBusy || collectPackageIds(detail).length === 0}
              onClick={async () => {
                const packageId = collectPackageIds(detail)[0];
                if (!packageId) return;
                setShipBusy(true);
                try {
                  const res = await tiktokPlatformAPI.getTiktokShippingDocument(activeShopId, packageId);
                  const data = res?.data && typeof res.data === 'object' ? res.data : res;
                  const url = data?.doc_url || data?.docUrl || data?.url || '';
                  if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } else {
                    showToast(t('orderList.noLabelUrl'), 'warning');
                  }
                } catch (e) {
                  showToast(e?.message || t('orderList.labelFailed'), 'error');
                } finally {
                  setShipBusy(false);
                }
              }}
            >
              {t('orderList.shippingLabel')}
            </button>
          ) : null}
        </div>
      </header>

      {error ? <div className="tko-ops-error" role="alert">{error}</div> : null}
      {loading && !detail ? (
        <div className="tko-return-detail__loading">{t('orderList.loadingDetail')}</div>
      ) : null}

      {detail ? (
        <>
          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{t('orderList.basic')}</h2>
            <KvTable rows={detailBasicRows(detail)} />
          </section>

          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{t('orderList.time')}</h2>
            <KvTable rows={detailTimeRows(detail)} />
          </section>

          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{t('orderList.priceFees')}</h2>
            <KvTable rows={detailPriceRows(detail)} />
            <div className="tko-order-detail__price-action">
              <button
                type="button"
                className="tko-btn tko-btn--secondary tko-btn--sm"
                disabled={!activeShopId || !orderNo}
                onClick={async () => {
                  try {
                    const res = await tiktokPlatformAPI.getOrderPriceDetail(activeShopId, orderNo);
                    const data = res?.data && typeof res.data === 'object' ? res.data : res;
                    const text = (node) => {
                      if (node == null || node === '') return '—';
                      if (typeof node === 'object') {
                        return `${node.amount ?? node.value ?? ''} ${node.currency || ''}`.trim() || '—';
                      }
                      return String(node);
                    };
                    setPriceDetail({
                      subtotal: text(data.subtotal || data.sku_list_price),
                      shipping: text(data.shipping_fee || data.shipping_list_price),
                      platform: text(data.platform_discount),
                      seller: text(data.seller_discount),
                      payment: text(data.payment || data.total),
                    });
                  } catch (e) {
                    showToast(e?.message || fixedUi('加载价格明细失败'), 'error');
                  }
                }}
              >
                {fixedUi('查看平台价格明细')}
              </button>
            </div>
            {priceDetail ? (
              <KvTable rows={[
                { label: fixedUi('商品金额'), value: priceDetail.subtotal || '—' },
                { label: fixedUi('运费'), value: priceDetail.shipping || '—' },
                { label: fixedUi('平台折扣'), value: priceDetail.platform || '—' },
                { label: fixedUi('卖家折扣'), value: priceDetail.seller || '—' },
                { label: fixedUi('买家实付'), value: priceDetail.payment || '—' },
              ]} />
            ) : null}
          </section>

          {actions.uploadWaybill ? (
          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{fixedUi("上传运单")}</h2>
            <div className="tko-order-detail__ship">
              <label className="tko-order-detail__field">
                <span>{fixedUi("物流商")}</span>
                {providers.length ? (
                  <select value={providerId} onChange={(e) => setProviderId(e.target.value)}>
                    <option value="">{fixedUi("请选择")}</option>
                    {providers.map((item) => (
                      <option key={item.id} value={item.id}>{item.name || item.id}</option>
                    ))}
                  </select>
                ) : (
                  <input placeholder={fixedUi("物流商 ID")} value={providerId} onChange={(e) => setProviderId(e.target.value)} />
                )}
              </label>
              <label className="tko-order-detail__field">
                <span>{fixedUi("运单号")}</span>
                <input value={trackingNo} onChange={(e) => setTrackingNo(e.target.value)} />
              </label>
              <button
                type="button"
                className="tko-btn tko-btn--primary"
                disabled={!activeShopId || !orderNo || shipBusy}
                onClick={async () => {
                  if (!trackingNo.trim() || !providerId.trim()) {
                    showToast(fixedUi("请填写物流商和运单号"), 'warning');
                    return;
                  }
                  setShipBusy(true);
                  try {
                    await tiktokPlatformAPI.markTiktokOrderShipped(activeShopId, orderNo, {
                      tracking_number: trackingNo.trim(),
                      shipping_provider_id: providerId.trim(),
                    });
                    showToast(fixedUi("运单已提交"), 'success');
                    await loadDetail();
                    setDetail((prev) => advanceOrderStatus(prev, 'IN_TRANSIT'));
                  } catch (e) {
                    showToast(e?.message || fixedUi('上传运单失败'), 'error');
                  } finally {
                    setShipBusy(false);
                  }
                }}
              >
                {fixedUi('提交运单')}
              </button>
            </div>
          </section>
          ) : null}

          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{t('orderList.receiveArea')}</h2>
            <KvTable rows={detailReceiveRows(detail)} />
          </section>

          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{t('orderList.goodsCount', { count: goodsList.length })}</h2>
            {goodsList.length === 0 ? (
              <p className="tko-return-detail__empty">{t('orderList.noGoods')}</p>
            ) : (
              <div className="tko-drafts__table-wrap tko-ops-table-wrap">
                <table className="tko-publish__table tko-drafts__table tko-ops-table tko-ops-table--order-goods">
                  <thead>
                    <tr>
                      <th scope="col">{t('orderList.productInfo')}</th>
                      <th scope="col">{t('tiktokCatalog.productId')}</th>
                      <th scope="col">{t('orderList.merchantSku')}</th>
                      <th scope="col">SKU ID</th>
                      <th scope="col">{t('orderList.spec')}</th>
                      <th scope="col">{fixedUi("售价")}</th>
                      <th scope="col">{t('orderList.goodsStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goodsList.map((goods, index) => {
                      const summary = listOrderProductSummary({ orderGoodsInfoList: [goods] });
                      const img = normalizeImageUrl(pickField(goods, 'spuPicURL', 'spuPicUrl', 'imageUrl'));
                      const statusCode = pickField(goods, 'displayStatus', 'display_status', 'newGoodsStatus', 'goodsStatus');
                      const attrText = goodsAttrText(goods) || pickField(goods, 'skuName', 'sku_name');
                      const salePrice = pickField(goods, 'salePrice', 'sale_price');
                      const priceCurrency = pickField(goods, 'currency') || pickField(detail, 'currency', 'saleCurrency');
                      return (
                        <tr key={String(pickField(goods, 'goodsId') || index)}>
                          <td className="tko-ops-td tko-ops-td--product">
                            <div className="tko-return-product">
                              {img ? (
                                <TiktokProductThumb
                                  imageUrl={img}
                                  className="tko-return-product__img"
                                  placeholderClassName="tko-return-product__img tko-return-product__img--empty"
                                  alt={summary.title || t('orderList.productImage')}
                                />
                              ) : (
                                <div className="tko-return-product__img tko-return-product__img--empty" />
                              )}
                              <div className="tko-return-product__meta">
                                <div className="tko-return-product__title" title={summary.title || undefined}>
                                  {summary.title || '—'}
                                </div>
                                <div className="tko-return-product__sku">
                                  {t('tiktokCatalog.productId')}：{summary.productId || pickField(goods, 'productId', 'product_id') || '—'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>{summary.productId || pickField(goods, 'productId', 'product_id') || '—'}</td>
                          <td>{pickField(goods, 'sellerSku', 'supplierSku', 'seller_sku') || '—'}</td>
                          <td>{pickField(goods, 'skuId', 'skuCode', 'sku_id') || '—'}</td>
                          <td title={attrText || undefined}>{attrText || '—'}</td>
                          <td>{salePrice ? `${priceCurrency ? `${priceCurrency} ` : ''}${salePrice}` : '—'}</td>
                          <td>
                            <span className={`tko-records__status tko-records__status--${statusModifier(statusCode)}`}>
                              {salesStatusLabel(statusCode) || '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{t('orderList.packageSection', { count: packages.length })}</h2>
            {packages.length === 0 ? (
              <p className="tko-return-detail__empty">{t('orderList.noPackages')}</p>
            ) : (
              packages.map((pkg, index) => {
                const packageNo = pickField(pkg, 'packageNo');
                const waybillNo = pickField(pkg, 'waybillNo', 'deliveryNo');
                const snap = tracks.find((t) => {
                  const p = String(pickField(t, 'packageNo') || '');
                  const w = String(pickField(t, 'waybillNo') || '');
                  if (packageNo && p === String(packageNo)) return true;
                  if (waybillNo && w === String(waybillNo)) return true;
                  return false;
                }) || tracks[index];
                return (
                  <div key={`${packageNo || waybillNo || index}`} className="tko-return-detail__section tko-return-detail__section--nested">
                    <KvTable
                      rows={[
                        { label: t('orderList.packageNo'), value: packageNo },
                        { label: t('orderList.waybillNo'), value: waybillNo },
                        { label: t('orderList.carrier'), value: pickField(pkg, 'carrier') },
                      ]}
                    />
                    <h3 className="tko-return-detail__section-title">{t('orderList.orderTrack')}</h3>
                    <TrackBlock snap={snap} />
                  </div>
                );
              })
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
